// ============================================================
// claimTypes.ts — THE claim type / route / track / complexity band labels
// (pure; claimTypes.test.ts). Mirrored byte-for-byte below this header in
// backend/src/lib/claimTypes.ts (server validation, audit, PDF) — a parity
// test (backend/test/claimTypes-parity.test.ts) keeps the two copies identical.
// The values are the B1 Prisma enums on Case (ClaimType, ClaimRoute,
// ClaimTrack; complexityBand is an Int 1–4).
// Claim type (what happened) and route (which procedure) are separate on
// purpose: picking a type only SUGGESTS a route, which stays editable.
// ============================================================

export const CLAIM_TYPES = [
  'RTA', 'EL_ACCIDENT', 'EL_DISEASE', 'PL', 'OCCUPIERS_LIABILITY', 'CLINICAL_NEGLIGENCE',
  'CICA', 'HOLIDAY_FOREIGN', 'MILITARY', 'ABUSE', 'CATASTROPHIC', 'FATAL', 'OTHER',
] as const
export type ClaimType = typeof CLAIM_TYPES[number]

export const CLAIM_TYPE_LABELS: Record<ClaimType, string> = {
  RTA: 'Road traffic (RTA)',
  EL_ACCIDENT: 'Employer’s liability – accident',
  EL_DISEASE: 'Employer’s liability – disease',
  PL: 'Public liability',
  OCCUPIERS_LIABILITY: 'Occupiers’ liability',
  CLINICAL_NEGLIGENCE: 'Clinical negligence',
  CICA: 'Criminal injuries (CICA)',
  HOLIDAY_FOREIGN: 'Holiday / foreign accident',
  MILITARY: 'Military',
  ABUSE: 'Abuse',
  CATASTROPHIC: 'Serious / catastrophic injury',
  FATAL: 'Fatal',
  OTHER: 'Other',
}
// Short labels for tight places (the case list card's details row, the list's
// claim type filter). One per type.
export const CLAIM_TYPE_SHORT_LABELS: Record<ClaimType, string> = {
  RTA: 'RTA',
  EL_ACCIDENT: 'EL accident',
  EL_DISEASE: 'EL disease',
  PL: 'PL',
  OCCUPIERS_LIABILITY: 'Occupiers’',
  CLINICAL_NEGLIGENCE: 'Clin neg',
  CICA: 'CICA',
  HOLIDAY_FOREIGN: 'Holiday',
  MILITARY: 'Military',
  ABUSE: 'Abuse',
  CATASTROPHIC: 'Catastrophic',
  FATAL: 'Fatal',
  OTHER: 'Other',
}

export const CLAIM_ROUTES = ['OIC', 'RTA_PORTAL', 'ELPL_PORTAL', 'PI_PROTOCOL', 'CLIN_NEG_PROTOCOL', 'MIB_UNTRACED', 'DISEASE_PROTOCOL', 'CICA_SCHEME'] as const
export type ClaimRoute = typeof CLAIM_ROUTES[number]

export const CLAIM_ROUTE_LABELS: Record<ClaimRoute, string> = {
  OIC: 'Official Injury Claim (OIC)',
  RTA_PORTAL: 'RTA portal',
  ELPL_PORTAL: 'EL/PL portal',
  PI_PROTOCOL: 'PI pre-action protocol',
  CLIN_NEG_PROTOCOL: 'Clinical negligence protocol',
  MIB_UNTRACED: 'Untraced driver (MIB)',
  DISEASE_PROTOCOL: 'Disease and illness protocol',
  CICA_SCHEME: 'CICA scheme',
}

// DB batch 2 — the kind of holiday / foreign claim (it sets the limitation
// rule) and the kind of abuse (sexual abuse of a child: s.11ZA).
export const HOLIDAY_REGIMES = ['PACKAGE', 'AIR', 'SEA', 'FOREIGN_LAW'] as const
export type HolidayRegime = typeof HOLIDAY_REGIMES[number]
export const HOLIDAY_REGIME_LABELS: Record<HolidayRegime, string> = {
  PACKAGE: 'Package holiday',
  AIR: 'Air travel',
  SEA: 'Sea travel',
  FOREIGN_LAW: 'Foreign law applies',
}
export const ABUSE_TYPES = ['SEXUAL', 'PHYSICAL', 'OTHER'] as const
export type AbuseType = typeof ABUSE_TYPES[number]
export const ABUSE_TYPE_LABELS: Record<AbuseType, string> = {
  SEXUAL: 'Sexual abuse',
  PHYSICAL: 'Physical abuse',
  OTHER: 'Other abuse',
}

export const CLAIM_TRACKS = ['SMALL_CLAIMS', 'FAST', 'INTERMEDIATE', 'MULTI'] as const
export type ClaimTrack = typeof CLAIM_TRACKS[number]

export const CLAIM_TRACK_LABELS: Record<ClaimTrack, string> = {
  SMALL_CLAIMS: 'Small claims',
  FAST: 'Fast track',
  INTERMEDIATE: 'Intermediate track',
  MULTI: 'Multi-track',
}

export const COMPLEXITY_BANDS = [1, 2, 3, 4] as const
export type ComplexityBand = typeof COMPLEXITY_BANDS[number]
export const complexityBandLabel = (band: number): string => `Band ${band}`

export const isClaimType = (v: unknown): v is ClaimType => typeof v === 'string' && (CLAIM_TYPES as readonly string[]).includes(v)
export const isClaimRoute = (v: unknown): v is ClaimRoute => typeof v === 'string' && (CLAIM_ROUTES as readonly string[]).includes(v)
export const isClaimTrack = (v: unknown): v is ClaimTrack => typeof v === 'string' && (CLAIM_TRACKS as readonly string[]).includes(v)
export const isComplexityBand = (v: unknown): v is ComplexityBand => typeof v === 'number' && (COMPLEXITY_BANDS as readonly number[]).includes(v)
export const isHolidayRegime = (v: unknown): v is HolidayRegime => typeof v === 'string' && (HOLIDAY_REGIMES as readonly string[]).includes(v)
export const isAbuseType = (v: unknown): v is AbuseType => typeof v === 'string' && (ABUSE_TYPES as readonly string[]).includes(v)

// Labels for a stored value (null / unknown → null, so callers choose the
// "not set" wording).
export const claimTypeLabel = (v: unknown): string | null => (isClaimType(v) ? CLAIM_TYPE_LABELS[v] : null)
export const claimTypeShortLabel = (v: unknown): string | null => (isClaimType(v) ? CLAIM_TYPE_SHORT_LABELS[v] : null)
export const claimRouteLabel = (v: unknown): string | null => (isClaimRoute(v) ? CLAIM_ROUTE_LABELS[v] : null)
export const claimTrackLabel = (v: unknown): string | null => (isClaimTrack(v) ? CLAIM_TRACK_LABELS[v] : null)
export const holidayRegimeLabel = (v: unknown): string | null => (isHolidayRegime(v) ? HOLIDAY_REGIME_LABELS[v] : null)
export const abuseTypeLabel = (v: unknown): string | null => (isAbuseType(v) ? ABUSE_TYPE_LABELS[v] : null)

// (Follow-ups 29 Oct) The routes each claim type can sensibly take — the
// Claim card and the new-case form offer only these, and the server refuses
// any other on create or when the type / route changes (an older case saved
// with a mismatch isn't blocked on an unrelated save). The type's suggested
// route (suggestedRoute) is always one of them. No type yet → every route
// except the untraced driver (MIB), which is road traffic only.
export const ROUTES_BY_TYPE: Record<ClaimType, readonly ClaimRoute[]> = {
  RTA: ['OIC', 'RTA_PORTAL', 'PI_PROTOCOL', 'MIB_UNTRACED'],
  EL_ACCIDENT: ['ELPL_PORTAL', 'PI_PROTOCOL'],
  PL: ['ELPL_PORTAL', 'PI_PROTOCOL'],
  OCCUPIERS_LIABILITY: ['ELPL_PORTAL', 'PI_PROTOCOL'],
  EL_DISEASE: ['DISEASE_PROTOCOL', 'ELPL_PORTAL', 'PI_PROTOCOL'],
  CLINICAL_NEGLIGENCE: ['CLIN_NEG_PROTOCOL'],
  CICA: ['CICA_SCHEME'],
  HOLIDAY_FOREIGN: ['PI_PROTOCOL'],
  MILITARY: ['PI_PROTOCOL'],
  ABUSE: ['PI_PROTOCOL'],
  CATASTROPHIC: ['PI_PROTOCOL'],
  FATAL: ['PI_PROTOCOL'],
  OTHER: ['PI_PROTOCOL'],
}
export const routesFor = (type: string | null | undefined): ClaimRoute[] =>
  isClaimType(type) ? [...ROUTES_BY_TYPE[type]] : CLAIM_ROUTES.filter((r) => r !== 'MIB_UNTRACED')
// No route is always fine (it's optional).
export const routeAllowedFor = (route: string | null | undefined, type: string | null | undefined): boolean =>
  !route || (routesFor(type) as string[]).includes(route)
// The plain-English refusal, naming the routes that do fit.
export const ROUTE_NOT_FOR_TYPE = 'That route doesn’t fit this claim type. Choose one of the routes listed for it.'
export const routeNotForTypeMessage = (route: string | null | undefined, type: string | null | undefined): string => {
  const r = isClaimRoute(route) ? CLAIM_ROUTE_LABELS[route] : 'That route'
  if (!isClaimType(type)) return `“${r}” is only for road traffic claims. Choose another route.`
  const fits = routesFor(type).map((x) => CLAIM_ROUTE_LABELS[x])
  const list = fits.length > 1 ? `${fits.slice(0, -1).join(', ')} or ${fits[fits.length - 1]}` : fits[0]
  return `“${r}” doesn’t fit the claim type “${CLAIM_TYPE_LABELS[type]}”. Choose ${list}.`
}
// The route a claim type usually takes — a SUGGESTION only (pre-filled on
// the new case form, always editable). CICA → its own scheme (no court).
export const suggestedRoute = (type: ClaimType): ClaimRoute | null => {
  switch (type) {
    case 'RTA': return 'RTA_PORTAL'
    case 'EL_ACCIDENT':
    case 'EL_DISEASE':
    case 'PL':
    case 'OCCUPIERS_LIABILITY': return 'ELPL_PORTAL'
    case 'CLINICAL_NEGLIGENCE': return 'CLIN_NEG_PROTOCOL'
    case 'CICA': return 'CICA_SCHEME'
    default: return 'PI_PROTOCOL'
  }
}
// (B3b) The route when the claim type changes: the new type's suggestion ONLY
// if the route was empty or still the PREVIOUS type's suggestion (i.e. never
// chosen by hand). A route someone chose is kept — unless the new type can't
// have it (MIB is RTA-only), when the suggestion replaces it.
export const routeOnTypeChange = (prevType: ClaimType | null | undefined, prevRoute: ClaimRoute | null | undefined, nextType: ClaimType, chosenByHand = false): ClaimRoute | null => {
  const wasSuggested = !prevRoute || (!!prevType && prevRoute === suggestedRoute(prevType))
  return prevRoute && (chosenByHand || !wasSuggested) && routeAllowedFor(prevRoute, nextType) ? prevRoute : suggestedRoute(nextType)
}

// Which claim fields APPLY to a claim type (+ the holiday regime) — B2b fix 2.
// The Claim card shows only these; when the type (or holiday regime) changes,
// the ones that no longer apply are CLEARED on Save (the card lists them
// first; the server clears them too — lib/caseClaim clearNotApplicable).
// Always kept (every type): claim type, route, date of knowledge, own date.
//   • CICA isn't a court claim → no track, complexity band or fixed costs;
//   • fixed recoverable costs aren't in force for clinical negligence yet.
//   • (Audit fix 1) the date of death is kept on FATAL, EL_DISEASE,
//     CLINICAL_NEGLIGENCE and CATASTROPHIC (it only moves the limitation
//     estimate on FATAL — on the others it adds a "consider Fatal" note);
//     the disease dates (first symptoms, diagnosis) on EL_DISEASE and
//     CLINICAL_NEGLIGENCE (where they can only make the estimate EARLIER).
export const TYPE_SPECIFIC_FIELDS = [
  'track', 'complexityBand', 'frcRegime', 'dateOfDeath', 'holidayRegime', 'travelArrivalDate', 'foreignLimitationMonths',
  'abuseType', 'policeFirstReportedAt', 'overseasOperation', 'firstSymptomsDate', 'diagnosisDate',
] as const
export type TypeSpecificField = typeof TYPE_SPECIFIC_FIELDS[number]
export const TYPE_SPECIFIC_FIELD_LABELS: Record<TypeSpecificField, string> = {
  track: 'Track',
  complexityBand: 'Complexity band',
  frcRegime: 'Fixed recoverable costs',
  dateOfDeath: 'Date of death',
  holidayRegime: 'Type of holiday claim',
  travelArrivalDate: 'Arrival date',
  foreignLimitationMonths: 'Foreign limitation period',
  abuseType: 'Type of abuse',
  policeFirstReportedAt: 'First reported to police',
  overseasOperation: 'Overseas operation',
  firstSymptomsDate: 'First symptoms',
  diagnosisDate: 'Diagnosis',
}
export const claimFieldApplies = (field: TypeSpecificField, c: { claimType?: string | null; holidayRegime?: string | null }): boolean => {
  const t = c.claimType ?? null
  switch (field) {
    case 'track':
    case 'complexityBand': return t !== 'CICA'
    case 'frcRegime': return t !== 'CICA' && t !== 'CLINICAL_NEGLIGENCE'
    case 'dateOfDeath': return t === 'FATAL' || t === 'EL_DISEASE' || t === 'CLINICAL_NEGLIGENCE' || t === 'CATASTROPHIC'
    case 'holidayRegime': return t === 'HOLIDAY_FOREIGN'
    case 'travelArrivalDate': return t === 'HOLIDAY_FOREIGN' && (c.holidayRegime === 'AIR' || c.holidayRegime === 'SEA')
    case 'foreignLimitationMonths': return t === 'HOLIDAY_FOREIGN' && c.holidayRegime === 'FOREIGN_LAW'
    case 'abuseType': return t === 'ABUSE'
    case 'policeFirstReportedAt': return t === 'CICA'
    case 'overseasOperation': return t === 'MILITARY'
    case 'firstSymptomsDate':
    case 'diagnosisDate': return t === 'EL_DISEASE' || t === 'CLINICAL_NEGLIGENCE'
  }
  return true
}

// Fixed recoverable costs: the extended regime applies to claims where the
// accident (cause of action) is on or after 1 October 2023. Only a
// SUGGESTION — the user confirms it. accidentIso = "YYYY-MM-DD" or null.
export const FRC_START = '2023-10-01'
export const frcSuggested = (accidentIso: string | null | undefined): boolean | null =>
  accidentIso ? accidentIso.slice(0, 10) >= FRC_START : null
